/**
 * TenantFormModal — create / edit a monitored tenant from the Admin Portal.
 *
 * Extracted from the legacy `TenantManagement.tsx` add/edit form so the new
 * `/admin?tab=tenants` drawer can launch the same flow without dragging the
 * whole legacy page into the portal. Until Wave E ships, the legacy page
 * keeps its inline form — this modal is the portal-side replacement.
 *
 * Mode is driven by `editingTenant`:
 *   - `null`  → Create. Calls `api.onboardTenant(payload)`.
 *   - object  → Edit.   Calls `api.updateTenant(editingTenant.tenantId, payload)`.
 *
 * The parent (the Admin Portal Tenants tab / drawer) owns React Query
 * invalidation; we just call `onSuccess(tenantResult)` after a successful
 * mutation so the parent can invalidate `tenantKeys.all` via `useTenants`.
 * Mutation calls go through `api.*` directly rather than the hook so this
 * component can be mounted standalone (e.g. from a drawer that didn't bring
 * a `useTenants` instance with it) and so the failure path stays local.
 *
 * Carbon: the Modal (layer-02, a pinned header, the footer button set with
 * Cancel and the submit), the field primitives, and InlineNotifications for
 * the save error (Coral, blocking) and the multi-tenant guidance (info).
 *
 * Voice:
 *   - Create title: "Add a tenant". Edit title: the tenant's name, so the
 *     subject leads.
 *   - Submit verbs: "Add tenant" / "Save changes", imperative.
 *   - Error notification: an editorial sentence, not a technical message.
 *   - Optional fields say so in their label; the rest are required.
 *
 * The Modal is a headless UI Dialog that portals itself. Opened from the
 * tenant drawer it is a nested dialog, stacked above the drawer.
 */

import { useEffect, useId, useState } from 'react'

import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Modal } from '@/components/carbon/Modal'
import { Select } from '@/components/carbon/Select'
import { TextInput } from '@/components/carbon/TextInput'
import { api } from '@/lib/api'
import OrgInlineDetection from '@/components/TenantManagement/OrgInlineDetection'
import type { TenantDto, TenantFormPayload } from '@/hooks/useTenants'

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

export interface TenantFormModalProps {
  isOpen: boolean
  onClose(): void
  /** null = create mode; non-null = edit mode. */
  editingTenant: TenantDto | null
  /**
   * Fired after a successful onboard / update. Parent invalidates the
   * tenant list cache via `useTenants` — we don't reach into React Query
   * from here so this component stays decoupled from the parent's data
   * layer choice.
   */
  onSuccess?: (tenant: TenantDto) => void
}

/* -------------------------------------------------------------------------- */
/* Form state                                                                 */
/* -------------------------------------------------------------------------- */

interface FormState {
  tenantId: string
  name: string
  domain: string
  authenticationMode: 'PerTenant' | 'MultiTenant'
  clientId: string
  certificateThumbprint: string
  notificationEmail: string
  autoTaskEmail: string
  rejectedDriftNotificationEmail: string
  driftAlertNotificationEmail: string
}

const EMPTY_FORM: FormState = {
  tenantId: '',
  name: '',
  domain: '',
  authenticationMode: 'MultiTenant',
  clientId: '',
  certificateThumbprint: '',
  notificationEmail: '',
  autoTaskEmail: '',
  rejectedDriftNotificationEmail: '',
  driftAlertNotificationEmail: '',
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function TenantFormModal({
  isOpen,
  onClose,
  editingTenant,
  onSuccess,
}: TenantFormModalProps) {
  const formId = useId()
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [isPending, setIsPending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isEditing = editingTenant !== null

  // Reset / seed on open. Anchored on `isOpen` + `editingTenant?.id` so
  // switching from one edit target to another also re-seeds cleanly.
  useEffect(() => {
    if (!isOpen) return
    setErrorMessage(null)
    setIsPending(false)
    if (editingTenant) {
      setForm({
        tenantId: editingTenant.tenantId,
        name: editingTenant.name,
        domain: editingTenant.domain ?? '',
        authenticationMode: editingTenant.authenticationMode ?? 'PerTenant',
        // Never pre-fill secrets — matches the legacy page's behaviour
        // (TenantManagement.tsx:229-230) so admins must re-enter them
        // deliberately on each edit.
        clientId: '',
        certificateThumbprint: '',
        notificationEmail: editingTenant.notificationEmail ?? '',
        autoTaskEmail: editingTenant.autoTaskEmail ?? '',
        rejectedDriftNotificationEmail:
          editingTenant.rejectedDriftNotificationEmail ?? '',
        driftAlertNotificationEmail:
          editingTenant.driftAlertNotificationEmail ?? '',
      })
    } else {
      setForm(EMPTY_FORM)
    }
  }, [isOpen, editingTenant])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isPending) return

    setIsPending(true)
    setErrorMessage(null)

    const payload: TenantFormPayload = {
      tenantId: form.tenantId.trim(),
      name: form.name.trim(),
      domain: form.domain.trim() || undefined,
      authenticationMode: form.authenticationMode,
      clientId: form.clientId.trim() || undefined,
      certificateThumbprint: form.certificateThumbprint.trim() || undefined,
      notificationEmail: form.notificationEmail.trim() || undefined,
      autoTaskEmail: form.autoTaskEmail.trim() || undefined,
      rejectedDriftNotificationEmail:
        form.rejectedDriftNotificationEmail.trim() || undefined,
      driftAlertNotificationEmail:
        form.driftAlertNotificationEmail.trim() || undefined,
    }

    try {
      const result: TenantDto = isEditing
        ? await api.updateTenant(editingTenant.tenantId, payload)
        : await api.onboardTenant(payload)

      onSuccess?.(result)
      onClose()
    } catch (err) {
      // Editorial sentence, not a stack-trace dump. The verbose message
      // (if any) is still visible in DevTools / App Insights.
      // eslint-disable-next-line no-console
      console.error('TenantFormModal save failed', err)
      setErrorMessage(
        isEditing
          ? "Couldn't save your changes. Check the values and try again."
          : "Couldn't add this tenant. Check the directory ID and name, then try again.",
      )
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      title={isEditing ? editingTenant.name : 'Add a tenant'}
      subtitle={isEditing ? 'Edit tenant' : 'Guardian starts monitoring it once it is saved and consented.'}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: isPending }}
      primaryAction={{
        label: isEditing ? 'Save changes' : 'Add tenant',
        type: 'submit',
        form: formId,
        loading: isPending,
      }}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-6">
        {errorMessage && (
          <InlineNotification kind="error" title={errorMessage} />
        )}

        <div className="grid grid-cols-1 gap-6 c-md:grid-cols-2">
          <div className="min-w-0">
            <TextInput
              id="tfm-tenantId"
              label="Tenant ID"
              required
              disabled={isEditing}
              value={form.tenantId}
              onChange={e => update('tenantId', e.target.value)}
              placeholder="223f854d-f453-4f15-a802-ca19dba2fd33"
              className="font-mono"
            />
            {/* Inline access-record check. Renders nothing until the field
                contains a plausible GUID; then surfaces match / missing /
                quick-create states. Suppressed during edit because the tid
                is fixed. */}
            {!isEditing && (
              <OrgInlineDetection
                tid={form.tenantId}
                seedName={form.name}
                seedDomain={form.domain}
              />
            )}
          </div>

          <TextInput
            id="tfm-name"
            label="Display name"
            required
            value={form.name}
            onChange={e => update('name', e.target.value)}
            placeholder="Acme Corporation"
          />

          <TextInput
            id="tfm-domain"
            label="Domain (optional)"
            value={form.domain}
            onChange={e => update('domain', e.target.value)}
            placeholder="acme.onmicrosoft.com"
          />

          <Select
            id="tfm-authMode"
            label="Authentication mode"
            value={form.authenticationMode}
            onChange={e =>
              update('authenticationMode', e.target.value as 'PerTenant' | 'MultiTenant')
            }
            options={[
              { value: 'MultiTenant', label: 'Multi-tenant (recommended)' },
              { value: 'PerTenant', label: 'Per-tenant (legacy)' },
            ]}
            helper={
              form.authenticationMode === 'MultiTenant'
                ? "Uses Guardian's shared app registration with admin consent."
                : 'Requires the customer to create their own app registration.'
            }
          />

          <TextInput
            id="tfm-notif"
            type="email"
            label="Notification email (optional)"
            value={form.notificationEmail}
            onChange={e => update('notificationEmail', e.target.value)}
            placeholder="alerts@acme.com"
            helper="Fallback for customer notifications when no users opt in."
          />

          <TextInput
            id="tfm-autotask"
            type="email"
            label="AutoTask email (optional)"
            value={form.autoTaskEmail}
            onChange={e => update('autoTaskEmail', e.target.value)}
            placeholder="autotask@iuvotech.com"
            helper="Address that creates AutoTask tickets from drift notifications."
          />

          <TextInput
            id="tfm-drift-alert"
            type="email"
            label="Drift alert notification email (optional)"
            value={form.driftAlertNotificationEmail}
            onChange={e => update('driftAlertNotificationEmail', e.target.value)}
            placeholder="alerts@customer.com"
            helper="Customer's ticketing inbox for new drift alerts."
          />

          <TextInput
            id="tfm-drift-reject"
            type="email"
            label="Rejected drift notification email (optional)"
            value={form.rejectedDriftNotificationEmail}
            onChange={e => update('rejectedDriftNotificationEmail', e.target.value)}
            placeholder="tickets@customer.com"
            helper="Customer's ticketing inbox for rejected drift notifications."
          />

          {/* Per-tenant credentials, only shown for PerTenant mode. */}
          {form.authenticationMode === 'PerTenant' && (
            <>
              <TextInput
                id="tfm-clientid"
                label="App registration client ID"
                required={form.authenticationMode === 'PerTenant'}
                value={form.clientId}
                onChange={e => update('clientId', e.target.value)}
                placeholder="12345678-1234-1234-1234-123456789012"
                className="font-mono"
              />
              <TextInput
                id="tfm-thumbprint"
                label="Certificate thumbprint (optional)"
                value={form.certificateThumbprint}
                onChange={e => update('certificateThumbprint', e.target.value)}
                placeholder="1234567890ABCDEF1234567890ABCDEF12345678"
                className="font-mono"
              />
            </>
          )}
        </div>

        {/* Multi-tenant guidance, shown when MultiTenant mode is selected. */}
        {form.authenticationMode === 'MultiTenant' && (
          <InlineNotification kind="info" title="Multi-tenant authentication.">
            After saving, generate a consent URL the customer&apos;s global administrator can use to
            grant permissions. No certificate transfer needed.
          </InlineNotification>
        )}
      </form>
    </Modal>
  )
}
